import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { urlGroupsApi } from "../../lib/api/network/urlGroups";
import { schedulesApi } from "../../lib/api/policies";
import { formatMessage, t } from "../../lib/i18n";
import { invalidatePolicyViews } from "../../lib/policyQueries";
import { useLocale } from "../../lib/useLocale";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { TypeaheadPicker } from "../ui/TypeaheadPicker";

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? t(`errors.${error.code}`, error.message) : t("errors.network");
}

/** Shared by the policy editor and creation wizard; groups never own an allow/deny mode. */
export function DomainGroupPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  useLocale();
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ["rules", "url-groups", "picker"], queryFn: urlGroupsApi.list, refetchOnWindowFocus: "always" });
  const create = useMutation({
    mutationFn: (name: string) => urlGroupsApi.create({ name: name.trim() }),
    onSuccess: (group) => {
      qc.setQueryData(["rules", "url-groups", "picker"], [...(groups.data ?? []), group]);
      onChange([...new Set([...value, group.id])]);
      void invalidatePolicyViews(qc);
    },
  });
  const selected = (groups.data ?? []).filter((g) => value.includes(g.id));
  return (
    <div className="stack">
      <TypeaheadPicker
        options={(groups.data ?? []).map((g) => ({ id: g.id, label: g.name,
          meta: formatMessage("operations.domains.memberCount", { count: String(g.members_count) }),
          disabled: !g.is_active && !value.includes(g.id), disabledReason: t("common.inactive") }))}
        selectedIds={new Set(value)}
        onChange={(ids) => onChange([...ids])}
        placeholder={t("operations.domains.searchGroups")}
        loading={groups.isFetching || create.isPending}
        onCreate={create.isPending ? undefined : (name) => create.mutate(name)}
        createLabel={(name) => formatMessage("operations.domains.createNamedGroup", { name })}
      />
      {(groups.error || create.error) && <Alert tone="danger">{errorMessage(groups.error || create.error)}</Alert>}
      {selected.some((g) => !g.is_active || g.members_count === 0) && (
        <Alert tone="warning">{t("operations.domains.emptyGroupWarning")}</Alert>
      )}
      <div className="row">
        {selected.map((g) => (
          <Link key={g.id} to={`/policies/groups?kind=url&group=${encodeURIComponent(g.id)}`} target="_blank" rel="noopener noreferrer">
            {formatMessage("operations.domains.editGroup", { name: g.name })}
          </Link>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={() => void groups.refetch()} disabled={groups.isFetching}>
          {t("operations.associations.refresh")}
        </Button>
      </div>
      <p className="muted">{t("operations.domains.policyHint")}</p>
    </div>
  );
}

export function PolicySchedulePicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  useLocale();
  const schedules = useQuery({ queryKey: ["rules", "schedules", "picker"], queryFn: () => schedulesApi.list(), refetchOnWindowFocus: "always" });
  const selected = schedules.data?.find((s) => s.id === value);
  return (
    <div className="stack">
      <TypeaheadPicker
        single
        options={(schedules.data ?? []).map((s) => ({ id: s.id, label: s.name,
          meta: `${s.timezone} / ${t(`scheduleDirection.${s.schedule_direction}`)}`,
          disabled: !s.is_active && s.id !== value, disabledReason: t("common.inactive") }))}
        selectedIds={new Set(value ? [value] : [])}
        onChange={(ids) => onChange([...ids][0] ?? "")}
        placeholder={t("rules.editor.selectSchedule")}
        loading={schedules.isFetching}
      />
      {schedules.error && <Alert tone="danger">{errorMessage(schedules.error)}</Alert>}
      {selected && (!selected.is_active || selected.windows_count === 0) && (
        <Alert tone="warning">{t("operations.schedules.emptyWarning")}</Alert>
      )}
      <div className="row">
        <Link to="/policies/schedules?id=new" target="_blank" rel="noopener noreferrer">{t("operations.schedules.create")}</Link>
        {value && <Link to={`/policies/schedules?id=${encodeURIComponent(value)}`} target="_blank" rel="noopener noreferrer">{t("operations.schedules.edit")}</Link>}
        <Button type="button" variant="ghost" size="sm" onClick={() => void schedules.refetch()} disabled={schedules.isFetching}>{t("operations.associations.refresh")}</Button>
      </div>
      <p className="muted">{t("operations.associations.newTab")}</p>
    </div>
  );
}
